// Tema global del rediseño CrediApp — ver design_handoff_crediapp_ui/README.md
// Reemplaza los createTheme ad-hoc previos (DrawerApp, Login, Unautorized, ServerOff).
// Uso: const theme = isDarkMode ? darkblueTheme : lightTheme;
import { createTheme } from '@mui/material/styles';

const shared = {
	typography: {
		fontFamily: "'IBM Plex Sans', sans-serif",
		h6: { fontSize: 15.5, fontWeight: 600, letterSpacing: '-.2px' },
		body1: { fontSize: 13.5 },
		body2: { fontSize: 12.5 },
		caption: { fontSize: 11.5 },
		button: { textTransform: 'none', fontWeight: 600, fontSize: 13 }
	},
	shape: { borderRadius: 10 }
};

const sharedComponents = (t) => ({
	MuiPaper: {
		styleOverrides: {
			root: {
				backgroundImage: 'none',
				borderRadius: 13,
				boxShadow: 'none',
				border: `1px solid ${t.line}`
			}
		}
	},
	MuiButton: {
		styleOverrides: {
			root: { borderRadius: 9, padding: '9px 16px' },
			containedPrimary: {
				boxShadow: '0 2px 8px rgba(0,161,82,.28)',
				'&:hover': { backgroundColor: '#00905f', boxShadow: '0 2px 8px rgba(0,161,82,.28)' }
			}
		}
	},
	MuiOutlinedInput: {
		styleOverrides: {
			root: {
				borderRadius: 10,
				backgroundColor: t.card,
				color: t.ink,
				'& fieldset': { borderColor: t.line2 },
				'&.Mui-focused fieldset': { borderColor: '#00a152' },
				'& input:-webkit-autofill': {
					WebkitBoxShadow: `0 0 0 100px ${t.card} inset`,
					WebkitTextFillColor: t.ink,
					caretColor: t.ink
				}
			}
		}
	},
	MuiSelect: {
		styleOverrides: {
			icon: { color: t.mut },
			select: { backgroundColor: 'transparent' }
		}
	},
	MuiMenuItem: {
		styleOverrides: {
			root: { color: t.ink }
		}
	},
	MuiInputLabel: {
		styleOverrides: {
			root: { color: t.mut }
		}
	},
	MuiChip: {
		styleOverrides: {
			root: { borderRadius: 20, fontSize: 11, fontWeight: 600, height: 22 }
		}
	},
	MuiTableCell: {
		styleOverrides: {
			head: {
				background: t.thead,
				color: t.mut,
				fontSize: 11,
				fontWeight: 600,
				textTransform: 'uppercase',
				letterSpacing: '.4px',
				borderBottom: `1px solid ${t.row}`
			},
			root: { borderBottom: `1px solid ${t.row}`, padding: '12px 20px', fontSize: 13 }
		}
	},
	MuiTab: { styleOverrides: { root: { textTransform: 'none', fontWeight: 600, fontSize: 13 } } }
});

export const lightTokens = {
	bg: '#eceef1',
	card: '#ffffff',
	line: '#e3e6eb',
	line2: '#dfe3e9',
	thead: '#f7f8fa',
	row: '#f2f3f6',
	soft: '#eef0f3',
	tabbar: '#e6e9ee',
	ink: '#182031',
	ink2: '#3a4557',
	ink3: '#5a6577',
	mut: '#8a94a3',
	mut2: '#9aa4b2',
	greenFg: '#00783c',
	greenBg: '#e3f5ec',
	greenLine: '#b7e0c8',
	redFg: '#c62828',
	redBg: '#fdeaea',
	redLine: '#f0c2c2',
	amberFg: '#b45309',
	amberBg: '#fef3e2',
	blueFg: '#1d4ed8',
	blueBg: '#e7eefe',
	hoverGreen: 'rgba(0,199,102,.06)'
};

export const darkTokens = {
	bg: '#0b1728',
	card: '#12233b',
	line: '#233650',
	line2: '#2b405c',
	thead: '#16283f',
	row: '#1d3049',
	soft: '#1e3149',
	tabbar: '#0e1c30',
	ink: '#e8eef6',
	ink2: '#c2cede',
	ink3: '#9cb0c6',
	mut: '#8095ab',
	mut2: '#71859b',
	greenFg: '#5fd99a',
	greenBg: 'rgba(0,199,102,.16)',
	greenLine: 'rgba(0,199,102,.4)',
	redFg: '#ff8585',
	redBg: 'rgba(229,72,77,.16)',
	redLine: 'rgba(229,72,77,.4)',
	amberFg: '#f5b04d',
	amberBg: 'rgba(245,158,11,.15)',
	blueFg: '#87acff',
	blueBg: 'rgba(90,140,255,.16)',
	hoverGreen: 'rgba(0,199,102,.06)'
};

export const lightTheme = createTheme({
	...shared,
	palette: {
		mode: 'light',
		primary: { main: '#00a152', dark: '#00905f', contrastText: '#fff' },
		success: { main: '#00783c', light: '#e3f5ec' },
		error: { main: '#c62828', light: '#fdeaea' },
		warning: { main: '#b45309', light: '#fef3e2' },
		info: { main: '#1d4ed8', light: '#e7eefe' },
		background: { default: lightTokens.bg, paper: lightTokens.card },
		text: { primary: lightTokens.ink, secondary: lightTokens.mut },
		divider: lightTokens.line
	},
	components: sharedComponents(lightTokens)
});

export const darkblueTheme = createTheme({
	...shared,
	palette: {
		mode: 'dark',
		primary: { main: '#00a152', dark: '#00905f', contrastText: '#fff' },
		success: { main: '#5fd99a', light: 'rgba(0,199,102,.16)' },
		error: { main: '#ff8585', light: 'rgba(229,72,77,.16)' },
		warning: { main: '#f5b04d', light: 'rgba(245,158,11,.15)' },
		info: { main: '#87acff', light: 'rgba(90,140,255,.16)' },
		background: { default: darkTokens.bg, paper: darkTokens.card },
		text: { primary: darkTokens.ink, secondary: darkTokens.mut },
		divider: darkTokens.line
	},
	components: sharedComponents(darkTokens)
});

// Constantes fuera del tema (sidebar navy — igual en ambos temas)
export const sidebar = {
	bg: '#0f1b2d',
	itemColor: '#9fb0c3',
	iconColor: '#7b8ca1',
	activeBg: 'rgba(0,199,102,.14)',
	activeBar: '#00c766',
	sectionColor: '#4f627a',
	footerBorder: 'rgba(255,255,255,.07)'
};

export const monoFont = "'IBM Plex Mono', monospace";

export const getAppTheme = (isDarkMode) => (isDarkMode ? darkblueTheme : lightTheme);
export const getTokens = (isDarkMode) => (isDarkMode ? darkTokens : lightTokens);

// Paleta para gráficas Chart.js (barras/línea/pastel). El relleno translúcido
// pensado para fondo blanco (alpha .2) se ve sucio/desaturado sobre el navy
// oscuro, así que el alpha del relleno sube bastante en modo oscuro.
const CHART_HUES = [
	[255, 99, 132],
	[54, 162, 235],
	[255, 206, 86],
	[75, 192, 192],
	[153, 102, 255],
	[255, 159, 64]
];

export const chartPalette = (isDarkMode, count = 6) => {
	const fillAlpha = isDarkMode ? 0.55 : 0.2;
	const bg = [];
	const border = [];
	for (let i = 0; i < count; i++) {
		const [r, g, b] = CHART_HUES[i % CHART_HUES.length];
		bg.push(`rgba(${r},${g},${b},${fillAlpha})`);
		border.push(`rgba(${r},${g},${b},1)`);
	}
	return { bg, border };
};

export const chartTextColor = (isDarkMode) => (isDarkMode ? darkTokens.ink2 : lightTokens.ink2);
export const chartGridColor = (isDarkMode) => (isDarkMode ? darkTokens.row : lightTokens.row);
